import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { fetchClientsData } from "../../Redux/Thunks/clientsThunk";
import { createProject } from "../../Redux/Thunks/projectManagementThunk";
import { fetchProjectsData } from "../../Redux/Thunks/projectManagementThunk";
import { selectClientsData } from "../../Redux/Slices/clientsSlice";
import { selectProjectsListForDropdown } from "../../Redux/Slices/projectsManagementSlice";
import { PROJECTS_LIST_PAGE_SIZE_STORAGE_KEY } from "../../Utility/Pagination/listPaginationConstants";
import { useListPageSize } from "../../hooks/useListPageSize";

/** Jira-like key from project name: "React Demo" -> "RD", "My Great Project" -> "MGP", "React" -> "REACT" (max 10 chars, A-Z only) */
function generateProjectKeyFromName(projectName) {
  if (!projectName || typeof projectName !== "string") return "";
  const trimmed = projectName.trim();
  if (!trimmed) return "";
  const words = trimmed.split(/\s+/).filter(Boolean);
  let key;
  if (words.length === 1) {
    key = words[0]
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase()
      .slice(0, 10);
  } else {
    key = words
      .map((w) => w.replace(/[^A-Za-z0-9]/g, "").charAt(0) || "")
      .join("")
      .toUpperCase()
      .slice(0, 10);
  }
  return key || "PROJ";
}

/** Return a key that is not in existingKeys; if baseKey is taken, append 1, 2, ... */
function getUniqueProjectKey(baseKey, existingKeys) {
  const set = new Set((existingKeys || []).map((k) => String(k).toUpperCase().trim()));
  let candidate = String(baseKey).toUpperCase().trim();
  if (!candidate) candidate = "PROJ";
  if (!set.has(candidate)) return candidate;
  let n = 1;
  while (set.has(candidate + n)) n++;
  return candidate + n;
}
import { AddProjectSchema } from "../../FormsValidations/Validations";
import Input from "../../Utility/Input/Input";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import FormSubmitButton from "../../Utility/Button/FormSubmitButton";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import YesNoToggle from "../../Utility/YesNoToggle/YesNoToggle";
import FileDropzone from "../../Utility/FileDropzone/FileDropzone";
import ErrorMsg from "../../Utility/ErrorMsg";
import SingleDatePicker from "../../Utility/DatePickerRange/SingleDatePicker";
import { DISPLAY_DATE_FORMAT_PICKER_MOMENT } from "../../utils/dateUtils";
import { logger } from "../../utils/logger";
import { showSuccess, showError } from "../../utils/toast";
import { canCreateProjectsForClient } from "../../constants/clientOnHoldRules";
import { validateUploadFiles } from "../../utils/fileUploadValidation";
import { getListFromApiResponse } from "./projectMappers";
import { fetchSystemList } from "../../Redux/Thunks/systemListsThunk";
import { selectSystemListItems } from "../../Redux/Slices/systemListsSlice";
import { mapSystemListItemToOption } from "../Issues/issueMappers";
import "../Clients/clients.css";
import BackArrow from "../../Utility/BackArrow/BackArrow";

export default function AddProject() {
  const [clientTagIds, setClientTagIds] = useState([]);
  const [vendorTagIds, setVendorTagIds] = useState([]);
  const [languageId, setLanguageId] = useState("");
  const [documents, setDocuments] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { pageSize: projectsListPageSize } = useListPageSize(PROJECTS_LIST_PAGE_SIZE_STORAGE_KEY);
  const clientsData = useSelector(selectClientsData);
  const projectsData = useSelector(selectProjectsListForDropdown);
  const clientTagList = useSelector((s) => selectSystemListItems(s, "CLIENT_TAG")) ?? [];
  const vendorTagList = useSelector((s) => selectSystemListItems(s, "VENDOR_TAG")) ?? [];
  const languageList = useSelector((s) => selectSystemListItems(s, "LANGUAGE")) ?? [];

  const clientTagOptions = useMemo(
    () => clientTagList.map((item) => mapSystemListItemToOption(item, {})),
    [clientTagList]
  );
  const vendorTagOptions = useMemo(
    () => vendorTagList.map((item) => mapSystemListItemToOption(item, {})),
    [vendorTagList]
  );
  const languageOptions = useMemo(
    () => languageList.map((item) => mapSystemListItemToOption(item, {})),
    [languageList]
  );
  const prevPath = location.state?.previousPath || "/projects";

  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(AddProjectSchema),
    defaultValues: {
      clientName: "",
      projectKey: "",
      projectName: "",
      startDate: "",
      endDate: "",
      hoursLimitPeriod: "Monthly",
      hoursLimit: "",
      status: "Active",
      version: "",
    },
  });

  // Fetch clients and projects (projects needed for unique project key); tag lists for multiselect
  useEffect(() => {
    dispatch(fetchClientsData({ getAll: true }));
    dispatch(fetchProjectsData({ getAll: true }));
    dispatch(
      fetchSystemList({
        listType: "CLIENT_TAG",
        context: "add-project-client-tags",
        force: true,
      })
    );
    dispatch(
      fetchSystemList({
        listType: "VENDOR_TAG",
        context: "add-project-vendor-tags",
        force: true,
      })
    );
    dispatch(
      fetchSystemList({
        listType: "LANGUAGE",
        context: "add-project-language",
        force: true,
      })
    );
  }, [dispatch]);

  const projectName = watch("projectName");
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const existingProjectKeys = (projectsData?.list ?? [])
    .map((p) => (p.projectKey ?? p.project_key ?? "").trim())
    .filter(Boolean);

  // Auto-generate Jira-like unique project key when project name changes
  useEffect(() => {
    if (!projectName || typeof projectName !== "string") return;
    const trimmed = projectName.trim();
    if (!trimmed) return;
    const baseKey = generateProjectKeyFromName(trimmed);
    const uniqueKey = getUniqueProjectKey(baseKey, existingProjectKeys);
    setValue("projectKey", uniqueKey, { shouldValidate: true });
  }, [projectName, setValue, existingProjectKeys.join(",")]);

  // End date cannot be before start date; clear end if start moves later
  useEffect(() => {
    if (!startDate || !endDate) return;
    if (String(endDate) < String(startDate)) {
      setValue("endDate", "", { shouldValidate: true });
    }
  }, [startDate, endDate, setValue]);

  // Map form hoursLimitPeriod to API enum (e.g. "Monthly" -> "MONTHLY")
  const toHoursLimitPeriod = (value) => {
    if (!value) return undefined;
    const map = { Monthly: "MONTHLY", Hourly: "HOURLY", Retainer: "RETAINER", Fixed: "FIXED" };
    return map[value] || value.toUpperCase().replace(/\s+/g, "_");
  };

  // Map form status to API enum (e.g. "Active" -> "ACTIVE", "On Hold" -> "ON_HOLD")
  const toStatus = (value) => {
    if (!value) return undefined;
    const map = {
      Active: "ACTIVE",
      Completed: "COMPLETED",
      "On Hold": "ON_HOLD",
      Archived: "ARCHIVED",
    };
    return map[value] || value.toUpperCase().replace(/\s+/g, "_");
  };

  const clientList = getListFromApiResponse(clientsData);

  const onSubmit = async (data) => {
    // Business rule: Cannot create new projects for a client that is On Hold
    const selectedClient = clientList.find((c) => (c.name ?? c.clientName) === data.clientName);
    if (selectedClient && !canCreateProjectsForClient(selectedClient)) {
      showError("Cannot create new projects. This client is On Hold.");
      return;
    }
    if (!selectedClient?.id) {
      showError("Please select a valid client.");
      return;
    }

    if (documents.length > 10) {
      showError("Maximum 10 files allowed.");
      return;
    }
    if (documents.length > 0) {
      const documentValidationError = await validateUploadFiles(documents, {
        inspectContent: false,
      });
      if (documentValidationError) {
        showError(documentValidationError);
        return;
      }
    }

    setLoading(true);
    try {
      const rawKey = (
        data.projectKey?.trim() ||
        generateProjectKeyFromName(data.projectName) ||
        "PROJ"
      ).toUpperCase();
      const projectKey = getUniqueProjectKey(rawKey, existingProjectKeys);

      const payload = {
        clientId: String(selectedClient.id),
        projectKey: String(projectKey),
        name: data.projectName?.trim(),
        startDate: data.startDate,
        endDate: data.endDate || null,
        hoursLimitPeriod: toHoursLimitPeriod(data.hoursLimitPeriod),
        hoursLimit:
          data.hoursLimit != null && data.hoursLimit !== "" ? Number(data.hoursLimit) : undefined,
        status: toStatus(data.status),
        clientTagIds: clientTagIds.map((x) => String(x)).filter(Boolean),
        vendorTagIds: vendorTagIds.map((x) => String(x)).filter(Boolean),
        languageIds: languageId ? [String(languageId)] : [],
        version: data.version?.trim() || undefined,
      };

      await dispatch(createProject({ ...payload, files: documents })).unwrap();
      showSuccess("Project created successfully");
      await dispatch(fetchProjectsData({ page: 1, limit: projectsListPageSize }));
      // Refresh full list used by work-item modals / dropdowns (`allListData`). Paged fetch alone
      // does not update `allListData`, so Create Task could show raw project id until hard refresh.
      await dispatch(fetchProjectsData({ getAll: true, force: true }));
      navigate(prevPath);
    } catch (error) {
      logger.error("Error adding project:", error);
      const message =
        error?.message || error?.response?.data?.message || "Failed to create project.";
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  // Only show clients that are not On Hold; display client name (API uses "name" e.g. "Infinity Group")
  const clientNames = clientList
    .filter((c) => c.onHold !== 1 && c.onHold !== true && canCreateProjectsForClient(c))
    .map((client) => client.name ?? client.clientName);

  // Pre-select the client from the Projects list's active Client filter (if one was passed in), once,
  // as soon as the client options are loaded. User can still change it afterward.
  const prefillClientName = location.state?.clientName;
  const didPrefillClientRef = useRef(false);
  useEffect(() => {
    if (didPrefillClientRef.current) return;
    if (!prefillClientName || clientNames.length === 0) return;
    if (clientNames.includes(prefillClientName)) {
      setValue("clientName", prefillClientName, { shouldValidate: true });
    }
    didPrefillClientRef.current = true;
  }, [prefillClientName, clientNames.join(","), setValue]);

  return (
    <div>
      <div className="w-full">
        <div className="p-0">
          <div className="w-full">
            <div className="flex flex-wrap -mx-4 mt-2 mb-2 items-center justify-between">
              <div className="w-full px-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <BackArrow to={prevPath} />
                  <Typography variant="h4" className="mb-0 text-theme-primary">
                    Add Project
                  </Typography>
                </div>
              </div>
            </div>

            <div className="relative flex flex-col min-w-0 bg-surface-primary rounded-md border border-border-primary shadow-sm mt-2 mb-5">
              <div className="flex-1 p-6">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="flex flex-wrap gap-4">
                    {/* Client Dropdown */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Controller
                        name="clientName"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            {...field}
                            label="Client"
                            options={clientNames}
                            placeholder="Select Client"
                            error={errors.clientName?.message}
                            required={true}
                            searchable={true}
                            className="text-sm"
                          />
                        )}
                      />
                    </div>

                    {/* Project Name */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="projectName"
                        className="font-medium text-text-primary mb-1"
                      >
                        Project Name
                        <Typography variant="span1" className="text-red-600 pl-1" component="span">
                          *
                        </Typography>
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("projectName")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.projectName ? "border-badge-danger" : ""
                          }`}
                          id="projectName"
                          placeholder="Enter project name"
                          label=""
                          error={errors.projectName?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.projectName?.message} className="mt-1" />
                    </div>

                    {/* Project Key (required by API; e.g. ONBRD) */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="projectKey"
                        className="font-medium text-text-primary mb-1"
                      >
                        Project Key
                        <Typography variant="span1" className="text-red-600 pl-1" component="span">
                          *
                        </Typography>
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("projectKey")}
                          readOnly
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-gray-100 cursor-default ${
                            errors.projectKey ? "border-badge-danger" : ""
                          }`}
                          id="projectKey"
                          placeholder="e.g. ONBRD"
                          label=""
                          error={errors.projectKey?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.projectKey?.message} className="mt-1" />
                    </div>

                    {/* Start Date */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography variant="text2" className="font-medium text-text-primary mb-1">
                        Start Date
                        <Typography variant="span1" className="text-red-600 pl-1" component="span">
                          *
                        </Typography>
                      </Typography>
                      <Controller
                        name="startDate"
                        control={control}
                        render={({ field }) => (
                          <SingleDatePicker
                            value={field.value ? { date: field.value } : null}
                            onChange={(val) => field.onChange(val?.date ?? "")}
                            placeholder={DISPLAY_DATE_FORMAT_PICKER_MOMENT}
                            className={errors.startDate ? "!border-red-500" : ""}
                          />
                        )}
                      />
                      <ErrorMsg message={errors.startDate?.message} className="mt-1" />
                    </div>

                    {/* End Date */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography variant="text2" className="font-medium text-text-primary mb-1">
                        End Date
                      </Typography>
                      <Controller
                        name="endDate"
                        control={control}
                        render={({ field }) => (
                          <SingleDatePicker
                            value={field.value ? { date: field.value } : null}
                            onChange={(val) => field.onChange(val?.date ?? "")}
                            minDate={startDate || undefined}
                            placeholder={DISPLAY_DATE_FORMAT_PICKER_MOMENT}
                            className={errors.endDate ? "!border-red-500" : ""}
                          />
                        )}
                      />
                      <ErrorMsg message={errors.endDate?.message} className="mt-1" />
                    </div>

                    {/* Hours Limit Period */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Controller
                        name="hoursLimitPeriod"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            {...field}
                            label="Hours Limit Period"
                            options={["Monthly", "Hourly", "Retainer", "Fixed"]}
                            // options={["Monthly"]}
                            placeholder="Select Hours Limit Period"
                            error={errors.hoursLimitPeriod?.message}
                            required={true}
                            searchable={false}
                            className="text-sm"
                          />
                        )}
                      />
                    </div>

                    {/* Hours Limit */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="hoursLimit"
                        className="font-medium text-text-primary mb-1"
                      >
                        Hours Limit
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="number"
                          {...register("hoursLimit")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.hoursLimit ? "border-badge-danger" : ""
                          }`}
                          id="hoursLimit"
                          placeholder="Enter hours limit"
                          min="0"
                          label=""
                          error={errors.hoursLimit?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.hoursLimit?.message} className="mt-1" />
                    </div>

                    {/* Status */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            {...field}
                            label="Status"
                            options={["Active", "Completed", "On Hold", "Archived"]}
                            placeholder="Select Status"
                            error={errors.status?.message}
                            required={true}
                            searchable={false}
                            className="text-sm"
                          />
                        )}
                      />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        className="font-medium text-text-primary block mb-1"
                      >
                        Client Tags
                      </Typography>
                      <Dropdown
                        name="clientTagIds"
                        value={clientTagIds}
                        onChange={(e) =>
                          setClientTagIds(Array.isArray(e.target?.value) ? e.target.value : [])
                        }
                        options={clientTagOptions}
                        placeholder="Search and select client tags"
                        multiselect
                        searchable
                        noWrapper
                        className="text-sm w-full"
                      />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        className="font-medium text-text-primary block mb-1"
                      >
                        Vendor Tags
                      </Typography>
                      <Dropdown
                        name="vendorTagIds"
                        value={vendorTagIds}
                        onChange={(e) =>
                          setVendorTagIds(Array.isArray(e.target?.value) ? e.target.value : [])
                        }
                        options={vendorTagOptions}
                        placeholder="Search and select vendor tags"
                        multiselect
                        searchable
                        noWrapper
                        className="text-sm w-full"
                      />
                    </div>

                    {/* Language */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        className="font-medium text-text-primary block mb-1"
                      >
                        Script Language
                      </Typography>
                      <Dropdown
                        name="languageId"
                        value={languageId}
                        onChange={(e) => setLanguageId(e.target?.value ?? "")}
                        options={languageOptions}
                        placeholder="Search and select language"
                        searchable
                        noWrapper
                        className="text-sm w-full"
                      />
                    </div>

                    {/* Version */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="version"
                        className="font-medium text-text-primary mb-1"
                      >
                        Version
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("version")}
                          className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                          id="version"
                          placeholder="Enter version"
                          label=""
                          noWrapper={true}
                        />
                      </div>
                    </div>

                    {/* Upload Documents */}
                    <div className="w-full mb-2">
                      <FileDropzone
                        label="Latest Security Scan"
                        onFilesChange={(files) => setDocuments(files)}
                        maxFiles={10}
                        maxSizeMB={10}
                        inspectContent={false}
                      />
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-wrap -mx-4 mt-3">
                    <div className="w-full px-4 text-end">
                      <div className="flex gap-2 justify-end">
                        <Button
                          type="button"
                          variant="danger"
                          size="md"
                          disabled={loading}
                          onClick={() => navigate(prevPath)}
                          className="btn-reset"
                        >
                          Cancel
                        </Button>
                        <FormSubmitButton
                          loading={loading}
                          label="Add Project"
                          idleIconClass="ri-add-circle-line"
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
